import React from "react";
import {
  Button,
  Collapse,
  Checkbox,
  Icon,
  Input,
  Popconfirm,
  Tooltip,
  Form,
} from "antd";
import { Draggable, Droppable } from "react-beautiful-dnd";
import get from "lodash/get";

import EventIntroEditor from "./EventIntroEditor";
import EventQuestionEditor from "./EventQuestionEditor";
import NextRulesContainer from "./NextRulesContainer";
import AskIfReadyEditor from "./AskIfReadyEditor";
import RuleLogicEditor from "./RuleLogicEditor";

const EventsList = ({
  form,
  definition,
  events,
  eventsById,
  lastAddedEventId,
  droppableId,
  getDraggableId,
  getEventDroppableId,
  getEventDraggableId,
  getEventFieldName,
  onEventIdChangeRequested,
  onAddEventAfterRequested,
  onAddQuestionChoiceRequested,
  onRemoveQuestionChoiceRequested,
  onAddQuestionNextRuleRequested,
  onRemoveQuestionNextRuleRequested,
  onRemoveEventRequested,
  onMediaSelectionRequested,
  onAddQuestionMetricRequested,
  onRemoveQuestionMetricRequested,
}) => (
  <Droppable droppableId={droppableId} type="event">
    {(provided) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className="c-scenario-editor__events-list"
      >
        {events.map((eventId, index) => {
          const event = eventsById[eventId];
          const draggableId = getDraggableId(event);
          return (
            <Draggable
              key={draggableId}
              draggableId={draggableId}
              index={index}
            >
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                >
                  <Collapse
                    defaultActiveKey={
                      lastAddedEventId === eventId ? 1 : undefined
                    }
                  >
                    <Collapse.Panel
                      key={1}
                      header={
                        <span>
                          {event.type}{" "}
                          <Input
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              onEventIdChangeRequested(event.id, e.target.value)
                            }
                            value={event.id}
                            style={{ width: "auto" }}
                          />
                        </span>
                      }
                      extra={
                        <>
                          <Tooltip title="Add event after this one">
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddEventAfterRequested(eventId);
                              }}
                            >
                              <Icon type="plus" />
                            </Button>
                          </Tooltip>
                          <Popconfirm
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onConfirm={(e) => {
                              e.stopPropagation();
                              onRemoveEventRequested(event);
                            }}
                            onCancel={(e) => {
                              e.stopPropagation();
                            }}
                            title="Do you really want to delete this event?"
                            cancelText="No"
                            okText="Yes"
                          >
                            <Button type="danger" size="small">
                              <Icon type="delete" />
                            </Button>
                          </Popconfirm>
                        </>
                      }
                    >
                      {(() => {
                        switch (event.type) {
                          case "intro":
                            return (
                              <>
                                <EventIntroEditor
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                  onMediaSelectionRequested={
                                    onMediaSelectionRequested
                                  }
                                />
                                <AskIfReadyEditor
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                />
                                <NextRulesContainer
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                  getEventDraggableId={getEventDraggableId}
                                  getEventDroppableId={getEventDroppableId}
                                  onAddQuestionNextRuleRequested={
                                    onAddQuestionNextRuleRequested
                                  }
                                  onRemoveQuestionNextRuleRequested={
                                    onRemoveQuestionNextRuleRequested
                                  }
                                  rules={[{ label: "Next" }]}
                                />
                                <RuleLogicEditor
                                  {...{ event, form, getEventFieldName }}
                                />
                              </>
                            );
                          case "question":
                            return (
                              <>
                                <EventQuestionEditor
                                  {...{
                                    event,
                                    form,
                                    getEventFieldName,
                                    getEventDraggableId,
                                    getEventDroppableId,
                                    onAddQuestionChoiceRequested,
                                    onRemoveQuestionChoiceRequested,
                                    onAddQuestionMetricRequested,
                                    onRemoveQuestionMetricRequested,
                                  }}
                                />
                                <AskIfReadyEditor
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                />
                                <NextRulesContainer
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                  getEventDraggableId={getEventDraggableId}
                                  getEventDroppableId={getEventDroppableId}
                                  onAddQuestionNextRuleRequested={
                                    onAddQuestionNextRuleRequested
                                  }
                                  onRemoveQuestionNextRuleRequested={
                                    onRemoveQuestionNextRuleRequested
                                  }
                                  rules={[
                                    {
                                      if: "all_correct",
                                      label: "If all correct",
                                    },
                                    {
                                      if: "some_correct",
                                      label: "If some correct",
                                    },
                                    {
                                      if: "none_correct",
                                      label: "If none correct",
                                    },
                                    { label: "Default next" },
                                  ]}
                                />
                                <RuleLogicEditor
                                  {...{ event, form, getEventFieldName }}
                                />
                              </>
                            );
                          case "leaderboard":
                            return (
                              <>
                                <Form
                                  labelCol={{ span: 3 }}
                                  wrapperCol={{ span: 21 }}
                                >
                                  <Form.Item label="Visible metrics">
                                    {form.getFieldDecorator(
                                      getEventFieldName(
                                        event,
                                        "visible_metrics.all"
                                      ),
                                      {
                                        valuePropName: "checked",
                                        initialValue: get(
                                          event,
                                          "visible_metrics.all",
                                          true
                                        ),
                                        validateTrigger: ["onBlur"],
                                      }
                                    )(<Checkbox>All</Checkbox>)}
                                    <br />
                                    {form.getFieldDecorator(
                                      getEventFieldName(
                                        event,
                                        "visible_metrics.selection"
                                      ),
                                      {
                                        initialValue: get(
                                          event,
                                          "visible_metrics.selection"
                                        ),
                                        validateTrigger: ["onBlur"],
                                      }
                                    )(
                                      <Checkbox.Group
                                        hidden={get(
                                          event,
                                          "visible_metrics.all",
                                          true
                                        )}
                                        options={[
                                          { value: "score", label: "Score" },
                                          ...get(definition, "metrics", []).map(
                                            ({ id, name }) => ({
                                              value: id,
                                              label: name || id,
                                            })
                                          ),
                                        ]}
                                      />
                                    )}
                                  </Form.Item>
                                </Form>
                                <AskIfReadyEditor
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                />
                                <NextRulesContainer
                                  event={event}
                                  form={form}
                                  getEventFieldName={getEventFieldName}
                                  getEventDraggableId={getEventDraggableId}
                                  getEventDroppableId={getEventDroppableId}
                                  onAddQuestionNextRuleRequested={
                                    onAddQuestionNextRuleRequested
                                  }
                                  onRemoveQuestionNextRuleRequested={
                                    onRemoveQuestionNextRuleRequested
                                  }
                                  rules={[{ label: "Next" }]}
                                />
                                <RuleLogicEditor
                                  {...{ event, form, getEventFieldName }}
                                />
                              </>
                            );
                          default:
                            return null;
                        }
                      })()}
                    </Collapse.Panel>
                  </Collapse>
                </div>
              )}
            </Draggable>
          );
        })}
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);

export default EventsList;
